
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from "@angular/router";
import { Inject, Injectable } from "@angular/core";
import { Location } from "@angular/common";
import { Observable, of } from "rxjs";
import { concatMap } from "rxjs/operators";

import { MsalBroadcastService, MsalGuardConfiguration, MsalService, MSAL_GUARD_CONFIG } from "@azure/msal-angular";
import { MANUAL_SECTION_ROLES } from '../../models/manual-nav.model';
import { BaseMsalGuard } from './base-msal.guard';

@Injectable({
  providedIn: 'root'
})
export class RolesMsalGuard extends BaseMsalGuard {
  constructor(
    @Inject(MSAL_GUARD_CONFIG) protected override msalGuardConfig: MsalGuardConfiguration,
    protected override msalBroadcastService: MsalBroadcastService,
    protected override authService: MsalService,
    protected override location: Location,
    protected override router: Router
  ) {
    super(msalGuardConfig, msalBroadcastService, authService, location, router);
  }

  private getAllowedRoles(route?: ActivatedRouteSnapshot): string[] | undefined {
    const section = route?.paramMap.get('section');
    if (section) {
      return MANUAL_SECTION_ROLES[section];
    }

    const configuredRoles = route?.data['allowedRoles'] ?? route?.data['expectedRoles'];
    return Array.isArray(configuredRoles) ? configuredRoles : undefined;
  }

  override activateHelper(state?: RouterStateSnapshot, route?: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    const result = super.activateHelper(state, route);
    const allowedRoles = this.getAllowedRoles(route);

    return result.pipe(
      concatMap((isAuthenticated) => {
        if (isAuthenticated !== true) {
          return of(isAuthenticated);
        }

        if (!allowedRoles || allowedRoles.length === 0) {
          return of(true);
        }

        let activeAccount = this.authService.instance.getActiveAccount();
        if (!activeAccount && this.authService.instance.getAllAccounts().length > 0) {
          activeAccount = this.authService.instance.getAllAccounts()[0];
        }

        const roles: string[] = activeAccount?.idTokenClaims?.roles || [];
        if (roles.length === 0) {
          window.alert('The token does not contain a roles claim. Ensure your account has an assigned role, then sign out and sign in again.');
          return of(false);
        }

        if (!roles.some(role => allowedRoles.includes(role))) {
          window.alert('You do not have permission to access this section.');
          return of(false);
        }

        return of(true);
      })
    );
  }
}
